import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Utilisateure } from "../../Modeles/UtilisateureModel";
import PasswordInput from "./InputPassword";

// ─── Smart Email/Phone Input ───────────────────────────────────────────────
interface SmartInputProps {
  value: string;
  onChange: (val: string) => void;
}

const SmartEmailPhoneInput: React.FC<SmartInputProps> = ({ value, onChange }) => {
  const [inputType, setInputType] = useState<"email" | "phone" | "unknown">("unknown");
  const [focused, setFocused] = useState(false);

  const detectType = (val: string) => {
    if (!val) return setInputType("unknown");
    // If starts with digit or +, treat as phone
    if (/^[\d+\s()-]/.test(val)) return setInputType("phone");
    // If contains @, treat as email
    if (val.includes("@")) return setInputType("email");
    return setInputType("unknown");
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    detectType(val);
    onChange(val);
  };

  const badge =
    inputType === "email"
      ? { label: "Email", color: "#1a1a2e" }
      : inputType === "phone"
      ? { label: "Téléphone", color: "#1a1a2e" }
      : null;

  return (
    <div style={{ position: "relative", marginBottom: "20px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          border: focused ? "2px solid #FFC300" : "2px solid #e8e8e8",
          borderRadius: "14px",
          backgroundColor: focused ? "#fffdf5" : "#fafafa",
          transition: "all 0.25s ease",
          overflow: "hidden",
          boxShadow: focused ? "0 0 0 4px rgba(255,195,0,0.12)" : "none",
        }}
      >
        {/* Icon area */}
        <div
          style={{
            padding: "14px 14px 14px 16px",
            color: inputType === "unknown" ? "#aaa" : "#FFC300",
            fontSize: "18px",
            transition: "color 0.2s",
            flexShrink: 0,
          }}
        >
          {inputType === "phone" ? "📱" : "✉️"}
        </div>

        <input
          type="text"
          inputMode={inputType === "phone" ? "tel" : "email"}
          value={value}
          onChange={handleChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder="Email ou numéro de téléphone"
          autoComplete="username"
          style={{
            flex: 1,
            border: "none",
            outline: "none",
            backgroundColor: "transparent",
            fontFamily: "'Sora', sans-serif",
            fontSize: "15px",
            fontWeight: "400",
            color: "#1a1a1a",
            padding: "14px 0",
            letterSpacing: "0.01em",
          }}
        />

        {/* Auto-detected badge */}
        {badge && (
          <div
            style={{
              marginRight: "12px",
              padding: "3px 10px",
              borderRadius: "20px",
              backgroundColor: badge.color,
              color: "#FFC300",
              fontSize: "11px",
              fontWeight: "700",
              fontFamily: "'Sora', sans-serif",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              flexShrink: 0,
            }}
          >
            {badge.label}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────
const RegisterMobile: React.FC<Utilisateure> = ({
  handleSubmit,
  setCompteEmail,
  CompteEmail,
  setPassword,
  Password,
  message,
  PopUp,
  commercialsList,
}) => {
  return (
    <>
      {/* Google Font import */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800&family=Playfair+Display:wght@700&display=swap');

        * { box-sizing: border-box; margin: 0; padding: 0; }

        .login-root {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          background: #ffffff;
          padding: 32px 24px;
          position: relative;
          overflow: hidden;
        }

        /* Decorative background blobs */
        .login-root::before {
          content: '';
          position: absolute;
          top: -80px;
          right: -80px;
          width: 280px;
          height: 280px;
          background: radial-gradient(circle, rgba(255,195,0,0.18) 0%, transparent 70%);
          border-radius: 50%;
          pointer-events: none;
        }
        .login-root::after {
          content: '';
          position: absolute;
          bottom: -60px;
          left: -60px;
          width: 220px;
          height: 220px;
          background: radial-gradient(circle, rgba(255,195,0,0.12) 0%, transparent 70%);
          border-radius: 50%;
          pointer-events: none;
        }

        .login-card {
          width: 100%;
          max-width: 400px;
          position: relative;
          z-index: 1;
          animation: slideUp 0.45s cubic-bezier(0.22, 1, 0.36, 1);
        }

        @keyframes slideUp {
          from { opacity: 0; transform: translateY(28px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .accent-bar {
          width: 44px;
          height: 5px;
          background: #FFC300;
          border-radius: 3px;
          margin-bottom: 20px;
        }

        .login-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 30px;
          font-weight: 700;
          line-height: 1.25;
          color: #0f0f0f;
          margin-bottom: 8px;
          letter-spacing: -0.02em;
        }

        .login-subtitle {
          font-family: 'Sora', sans-serif;
          font-size: 13.5px;
          color: #888;
          font-weight: 400;
          margin-bottom: 36px;
          letter-spacing: 0.01em;
        }

        .field-label {
          display: block;
          font-family: 'Sora', sans-serif;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: #555;
          margin-bottom: 8px;
        }

        .submit-btn {
          width: 100%;
          padding: 15px;
          margin-top: 10px;
          background: #FFC300;
          border: none;
          color: #0f0f0f;
          font-size: 15px;
          font-weight: 700;
          font-family: 'Sora', sans-serif;
          letter-spacing: 0.04em;
          cursor: pointer;
          border-radius: 14px;
          margin-bottom: 28px;
          position: relative;
          overflow: hidden;
          transition: transform 0.15s ease, box-shadow 0.15s ease;
          box-shadow: 0 4px 18px rgba(255,195,0,0.35);
        }
        .submit-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 24px rgba(255,195,0,0.45);
        }
        .submit-btn:active {
          transform: translateY(0px);
          box-shadow: 0 2px 10px rgba(255,195,0,0.3);
        }

        .divider {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 24px;
        }
        .divider-line {
          flex: 1;
          height: 1px;
          background: #ebebeb;
        }
        .divider-text {
          font-family: 'Sora', sans-serif;
          font-size: 11.5px;
          color: #bbb;
          font-weight: 500;
          letter-spacing: 0.06em;
          text-transform: uppercase;
        }

        .signup-text {
          text-align: center;
          font-family: 'Sora', sans-serif;
          font-size: 13.5px;
          color: #999;
          font-weight: 400;
        }
        .signup-link {
          color: #0f0f0f;
          font-weight: 600;
          text-decoration: none;
          border-bottom: 1.5px solid #FFC300;
          padding-bottom: 1px;
          transition: color 0.15s;
        }
        .signup-link:hover { color: #FFC300; }

        /* Error popup */
        .error-banner {
          display: flex;
          align-items: center;
          gap: 10px;
          background: #fff4f4;
          border: 1.5px solid #ffcccc;
          border-radius: 12px;
          padding: 12px 16px;
          margin-bottom: 20px;
          font-family: 'Sora', sans-serif;
          font-size: 13px;
          color: #cc2222;
          font-weight: 500;
          animation: shake 0.35s ease;
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25%       { transform: translateX(-6px); }
          75%       { transform: translateX(6px); }
        }
      `}</style>

      <div className="login-root">
        <div className="login-card">
          {/* Header */}
          <div className="accent-bar" />
          <h1 className="login-title">
            Connectez-vous<br />à votre compte
          </h1>
          <p className="login-subtitle">Bienvenue ! Veuillez saisir vos informations.</p>

          <form onSubmit={handleSubmit}>
            {/* Error Banner */}
            {PopUp && (
              <div className="error-banner">
                <span style={{ fontSize: "16px" }}>⚠️</span>
                <span>{message || "Identifiants incorrects. Veuillez réessayer."}</span>
              </div>
            )}

            {/* Email / Phone */}
            <label className="field-label">Email ou téléphone</label>
            <SmartEmailPhoneInput
              value={CompteEmail}
              onChange={(val) => setCompteEmail(val)}
            />

            {/* Password */}
            <label className="field-label">Mot de passe</label>
            <div style={{ marginBottom: "8px" }}>
              <PasswordInput
                value={Password}
                onChange={(newValue: any) => setPassword(newValue)}
              />
            </div>

            {/* Forgot password */}
            <div style={{ textAlign: "right", marginBottom: "24px" }}>
              <Link
                to="/mote_de_passe_oublie"
                style={{
                  fontFamily: "'Sora', sans-serif",
                  fontSize: "12.5px",
                  color: "#888",
                  textDecoration: "none",
                  fontWeight: "500",
                  borderBottom: "1px dashed #ccc",
                  paddingBottom: "1px",
                }}
              >
                Mot de passe oublié ?
              </Link>
            </div>

            <button type="submit" className="submit-btn">
              Se connecter →
            </button>
          </form>

          <div className="divider">
            <div className="divider-line" />
            <span className="divider-text">Nouveau ?</span>
            <div className="divider-line" />
          </div>

        </div>
      </div>
    </>
  );
};

export default RegisterMobile;